import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createEditor } from 'slate'

import { Editable, withReact, Slate } from 'slate-react'
import { withHistory } from 'slate-history'

import store from '@/core/store'
import isEqual from 'lodash.isequal'

import { withImages, Image } from './ImageUpload'
import { EditorToolbar } from './EditorToolbar'
import editorService from '../editor.service'
import AttachmentSection from '../../AttachmentSection/AttachmentSection'
import ForwardMessagePreview from '../../ForwardMessagePreview/ForwardMessagePreview'
import { useDeviceSelectors } from 'react-device-detect'
import { AndroidToolbar } from './AndroidToolbar'

export const SlateEditor = (props) => {
  const [selectors] = useDeviceSelectors(window.navigator.userAgent)
  const { isReply, parentMessageId } = props
  const [isDirty, setIsDirty] = useState(false)
  const initialValue = props.initialHtml || [
    {
      type: 'paragraph',
      children: [
        { text: '' }
      ],
    }
  ]

  const [value, setValue] = useState(props.slateContent || initialValue)

  const renderElement = useCallback(props => <Element {...props} />, [])
  const renderLeaf = useCallback(props => <Leaf {...props} />, [])
  const editor = useMemo(() => withImages(withHistory(withReact(createEditor()))), [])

  const onEditorValueChange = props.onEditorValueChange

  const Element = (props) => {
    const { attributes, children, element } = props
    const style = { textAlign: element.align }
    switch (element.type) {
      case 'block-quote':
        return <blockquote style={style} {...attributes}>{children}</blockquote>
      case 'bulleted-list':
        if (isReply) return <ul style={style} className="list-disc list-inside" {...attributes}>{children}</ul>
        else return <ul style={style} {...attributes}>{children}</ul>
      case 'heading-one':
        return <h1 style={style} {...attributes}>{children}</h1>
      case 'heading-two':
        return <h2 style={style} {...attributes}>{children}</h2>
      case 'list-item':
        return <li style={style} {...attributes}>{children}</li>
      case 'image':
        return <Image {...props} />
      case 'numbered-list':
        if (isReply) return <ol style={style} className="list-decimal list-inside" {...attributes}>{children}</ol>
        else return <ol style={style} {...attributes}>{children}</ol>
      default:
        return <p style={{ ...style, marginTop: '0.2em', marginBottom: '0.2em' }} {...attributes}>{children}</p>
    }
  }

  const Leaf = (props) => {
    let { attributes, children, leaf } = props
    if (leaf.bold) {
      children = <strong>{children}</strong>
    }
  
    if (leaf.italic) {
      children = <em>{children}</em>
    }
  
    if (leaf.underline) {
      children = <u>{children}</u>
    }

    if (leaf.strikethrough) {
      children = <del>{children}</del>
    }
    return <span style={{ color: leaf?.color?.hex }} {...attributes}>{children}</span>
  }

  useEffect(() => {
    store.on('STANDALONE_MESSAGE:SENT', () => {
      const point = { path: [0, 0], offset: 0 }
      editor.selection = { anchor: point, focus: point }
      editor.history = { redos: [], undos: [] }
      setValue(initialValue)
    })
  }, [])

  useEffect(() => {
    !isDirty && setValue(props.slateContent || initialValue)
  }, [props.slateContent])

  return (
    <> {!selectors.isAndroid
      ? <Slate editor={editor} value={value} onChange={(changedValue) => {
        if (isEqual(changedValue, value)) {
          return
        }
        setIsDirty(true)
        onEditorValueChange(changedValue)
        setValue(changedValue)
      }}>
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          spellCheck
          id="body-editor"
          onKeyDown={(event) => editorService.onKeyDownHandler(event, editor)}
          className='editable-slate-element'
        />
      
        {props.draft?.type === 'forward' && <ForwardMessagePreview draft={props.draft}/>}
        <AttachmentSection draft={props.draft}/>
        <EditorToolbar parentMessageId={parentMessageId} isAndroid={selectors.isAndroid}/>
      </Slate>
      : <>
        <textarea placeholder='Write your message here ...' onChange={(event) => {
          const changedValue = event.target.value
          setIsDirty(true)
          setValue(changedValue)
          props.onChangeMessageText(changedValue)
        }} className='editable-slate-element'></textarea>
        {props.draft?.type === 'forward' && <ForwardMessagePreview draft={props.draft}/>}
        <AttachmentSection draft={props.draft}/>
        <AndroidToolbar/>
      </>}
    </>

  )
}
